import './Dashboard.scss';

import { faBorderAll, faMinus } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import useAxiosHook from '../../../hooks/useAxiosHook';
import useHaveValuesChanged from '../../../hooks/useHaveValuesChanged';
import usePageTitle from '../../../hooks/usePageTitle';
import useSearchQueryDateParam from '../../../hooks/useSearchQueryDateParam';
import { DashboardData } from '../../../types/api/dashboard';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../utils/constants/routePaths';
import { httpDateFormat } from '../../../utils/helpers/formatting';
import { queryString } from '../../../utils/helpers/http';
import { renderIcon } from '../../../utils/helpers/icon';
import { httpQueryObject } from '../../../utils/helpers/misc';
import {
  getSearchQueryParam,
  tryDateSearchParam,
} from '../../../utils/helpers/searchQuery';
import DialogSpinner from '../../Dialogs/DialogSpinner/DialogSpinner';
import Flex from '../../layout/flex/Flex';
import MainContent from '../../layout/flex/MainContent';
import HeaderPages from '../Components/HeaderPages/HeaderPages';
import { bgColors } from './Dashboard.functions';

function Dashboard() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  usePageTitle(t('Dashboard'));
  const [dateFrom, setDateFrom] = useState<Date | null>(
    () =>
      tryDateSearchParam(
        getSearchQueryParam(location.search, 'date_from') ?? ''
      ) ??
      dayjs()
        .set('day', dayjs().day() - 7)
        .toDate() // default 7 days before
  );
  const [dateTo, setDateTo] = useState<Date | null>(
    () =>
      tryDateSearchParam(
        getSearchQueryParam(location.search, 'date_to') ?? ''
      ) ?? dayjs().toDate()
  );

  const filtersArr = useMemo(() => [dateFrom, dateTo], [dateFrom, dateTo]);
  const haveFiltersChanged = useHaveValuesChanged(filtersArr);

  const httpFiltersObj = useMemo(() => {
    const _dateFrom = dayjs(dateFrom as any);
    const _dateTo = dayjs(dateTo as any);
    return {
      date_from: httpDateFormat(_dateFrom),
      date_to: httpDateFormat(_dateTo),
      page: haveFiltersChanged ? 1 : 1,
      limit: 100,
    };
  }, [haveFiltersChanged, dateFrom, dateTo]);

  useSearchQueryDateParam('date_from', dateFrom);
  useSearchQueryDateParam('date_to', dateTo);

  const canLoadData = httpFiltersObj.date_from && httpFiltersObj.date_to;

  const { data, isLoading, reload } = useAxiosHook<DashboardData[]>(
    {
      url:
        '/reports/orders/state' + queryString(httpQueryObject(httpFiltersObj)),
    },
    {
      skipWhen: !canLoadData,
    }
  );

  const onClickCard = useCallback(
    (state) => {
      if (!state) {
        return;
      }
      history.push(
        constructIdRoute(RoutePaths.DashboardDetails, JSON.stringify(state)) +
          queryString(httpFiltersObj)
      );
    },
    [history, httpFiltersObj]
  );

  // const groupMap: Record<string, string> = {
  //   today: 'Today',
  //   collected: 'Collected',
  //   warehouse: 'warehouse',
  //   pending: 'Pending',
  //   courier: 'Courier',
  //   in: 'In Transit',
  //   return: 'Returns & Issues',
  //   reject: 'Returns & Issues',
  //   unsuccess: 'Returns & Issues',
  // };

  // const groupedStats = data?.reduce((acc: Record<string, any[]>, stat: any) => {
  //   const groupKey =
  //     Object.keys(groupMap).find((key) =>
  //       stat.key.toLowerCase().includes(key)
  //     ) || 'Other';

  //   const groupTitle = groupMap[groupKey] || 'Other';

  //   if (!acc[groupTitle]) acc[groupTitle] = [];
  //   acc[groupTitle].push(stat);
  //   return acc;
  // }, {});

  return (
    <div className="page dashboard">
      <HeaderPages title={t('Dashboard')} icon={faBorderAll} />

      <Flex direction="column">
        <div className="sidebar_filters">
          <div className="sidebar_filter">
            <div className="sidebar_filter_datepickers">
              <Calendar
                id="filter_date_from"
                value={dateFrom ?? undefined}
                monthNavigator
                yearNavigator
                dateFormat="dd/mm/yy"
                yearRange={`2009:${dayjs().format('YYYY')}`}
                maxDate={new Date()}
                onChange={(e) => setDateFrom(e.value as Date | null)}
                inputClassName={classNames({
                  sidebar_filter_active: dateFrom,
                })}
              />
              <span style={{ margin: 'auto' }}>{renderIcon(faMinus)}</span>
              <Calendar
                id="filter_date_to"
                value={dateTo ?? undefined}
                onChange={(e) => {
                  setDateTo(e.value as Date | null);
                }}
                dateFormat="dd/mm/yy"
                maxDate={new Date()}
                monthNavigator
                yearNavigator
                yearRange={`2009:${dayjs().format('YYYY')}`}
                inputClassName={classNames({
                  sidebar_filter_active: dateTo,
                })}
              />
            </div>
          </div>
          <div>
            <Button
              type="button"
              icon="fas fa-sync-alt"
              tooltip={t('Reload')}
              tooltipOptions={{ position: 'top' }}
              onClick={() => reload()}
              className="p-mx-2 p-button-text p-button-plain"
            />
          </div>
        </div>
        <MainContent>
          {isLoading && <DialogSpinner />}
          <div className="stat-boxes">
            {!isLoading &&
              data &&
              data?.map((stat: any) => (
                <div
                  key={stat.key}
                  className={`stat-box `}
                  style={{ backgroundColor: bgColors[stat.key] }}
                  onClick={() => onClickCard(stat)}
                >
                  <div className="stat-content">
                    <div className="stat-count">{stat.count ?? 0}</div>
                    <div className="stat-desc">{stat.description ?? ''}</div>
                  </div>
                </div>
              ))}
          </div>
        </MainContent>
      </Flex>
    </div>
  );
}

export default Dashboard;
